import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../api/axios";

function Projects() {
  const { hasPermission } = useAuth();
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [salesSearchTerm, setSalesSearchTerm] = useState("");
  const [developerSearchTerm, setDeveloperSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [filterOverdue, setFilterOverdue] = useState('all'); // 'all', 'overdue', 'not-overdue'
  const [filterMonth, setFilterMonth] = useState('all'); // 'all' or specific month in format 'YYYY-MM'
  const [filterEmployee, setFilterEmployee] = useState('all'); // 'all' or employee ID
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    assignedEmployees: [],
    startDate: "",
    deadline: "",
    status: "active",
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const isAdmin = hasPermission("goals", "delete");

  // Check if project is overdue
  const isProjectOverdue = (project) => {
    if (!project.deadline) return false;
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const deadline = new Date(project.deadline);
    return deadline < today && project.status !== 'completed';
  };

  useEffect(() => {
    fetchProjects();
    if (isAdmin) {
      fetchEmployees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await api.get("/projects");
      setProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get("/employees");
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("description", formData.description);
      formDataToSend.append(
        "assignedEmployees",
        JSON.stringify(formData.assignedEmployees)
      );
      formDataToSend.append("startDate", formData.startDate);
      formDataToSend.append("deadline", formData.deadline);
      formDataToSend.append("status", formData.status);

      // Append files
      selectedFiles.forEach((file) => {
        formDataToSend.append("documents", file);
      });

      if (editingProject) {
        await api.put(`/projects/${editingProject._id}`, formDataToSend, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await api.post("/projects", formDataToSend, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      setShowModal(false);
      setEditingProject(null);
      setSelectedFiles([]);
      setFormData({
        name: "",
        description: "",
        assignedEmployees: [],
        startDate: "",
        deadline: "",
        status: "active",
      });
      fetchProjects();
    } catch (error) {
      alert(error.response?.data?.message || "Error saving project");
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setSelectedFiles([]);
    setFormData({
      name: project.name,
      description: project.description || "",
      assignedEmployees: project.assignedEmployees.map((e) => e._id),
      startDate: new Date(project.startDate).toISOString().split("T")[0],
      deadline: new Date(project.deadline).toISOString().split("T")[0],
      status: project.status,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this project?")) {
      try {
        await api.delete(`/projects/${id}`);
        fetchProjects();
      } catch (error) {
        alert("Error deleting project");
      }
    }
  };

  const handleEmployeeToggle = (employeeId) => {
    setFormData((prev) => ({
      ...prev,
      assignedEmployees: prev.assignedEmployees.includes(employeeId)
        ? prev.assignedEmployees.filter((id) => id !== employeeId)
        : [...prev.assignedEmployees, employeeId],
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) => {
      const ext = file.name.split(".").pop().toLowerCase();
      if (!["pdf", "docx", "doc"].includes(ext)) {
        alert(
          `${file.name} is not a valid file type. Only PDF and DOCX files are allowed.`
        );
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert(`${file.name} is too large. Maximum file size is 10MB.`);
        return false;
      }
      return true;
    });
    setSelectedFiles((prev) => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const downloadDocument = async (projectId, docId, filename) => {
    try {
      const response = await api.get(
        `/projects/${projectId}/documents/${docId}`,
        {
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert("Error downloading document");
    }
  };

  const deleteDocument = async (projectId, docId) => {
    if (window.confirm("Are you sure you want to delete this document?")) {
      try {
        await api.delete(`/projects/${projectId}/documents/${docId}`);
        fetchProjects();
      } catch (error) {
        alert("Error deleting document");
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "#28a745";
      case "completed":
        return "#007bff";
      case "on-hold":
        return "#ffc107";
      default:
        return "#666";
    }
  };

  const handleViewDetails = (project) => {
    setSelectedProject(project);
    setShowDetailsModal(true);
  };

  const calculateProgress = (project) => {
    if (project.status === 'completed') return 100;
    if (project.status === 'on-hold') return 0;
    
    const startDate = new Date(project.startDate);
    const deadline = new Date(project.deadline);
    const today = new Date();
    
    const totalDuration = deadline - startDate;
    const elapsed = today - startDate;
    
    if (elapsed < 0) return 0;
    if (elapsed > totalDuration) return 100;
    
    return Math.round((elapsed / totalDuration) * 100);
  };

  const exportToExcel = () => {
    const data = filteredProjects.map(project => ({
      'Project Name': project.name,
      'Description': project.description || '-',
      'Status': project.status.toUpperCase(),
      'Start Date': new Date(project.startDate).toLocaleDateString(),
      'Deadline': new Date(project.deadline).toLocaleDateString(),
      'Assigned Employees': project.assignedEmployees.map(e => `${e.firstName} ${e.lastName}`).join(', '),
      'Documents': project.documents?.length || 0,
      'Progress': calculateProgress(project) + '%'
    }));

    const csvContent = [
      Object.keys(data[0]).join(','),
      ...data.map(row => Object.values(row).map(val => `"${val}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `projects_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    setShowExportMenu(false);
  };

  const exportToPDF = () => {
    const printWindow = window.open('', '_blank');
    const projectsHTML = filteredProjects.map(project => `
      <div style="margin-bottom: 30px; border: 1px solid #ddd; padding: 20px; border-radius: 8px;">
        <h3 style="color: #667eea; margin-bottom: 10px;">${project.name}</h3>
        <p><strong>Description:</strong> ${project.description || '-'}</p>
        <p><strong>Status:</strong> ${project.status.toUpperCase()}</p>
        <p><strong>Start Date:</strong> ${new Date(project.startDate).toLocaleDateString()}</p>
        <p><strong>Deadline:</strong> ${new Date(project.deadline).toLocaleDateString()}</p>
        <p><strong>Progress:</strong> ${calculateProgress(project)}%</p>
        <p><strong>Assigned Employees:</strong> ${project.assignedEmployees.map(e => `${e.firstName} ${e.lastName} (${e.employeeId})`).join(', ')}</p>
        <p><strong>Documents:</strong> ${project.documents?.length || 0}</p>
      </div>
    `).join('');

    printWindow.document.write(`
      <html>
        <head>
          <title>Projects Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #667eea; text-align: center; }
            .meta { text-align: center; color: #666; margin-bottom: 30px; }
          </style>
        </head>
        <body>
          <h1>📋 Projects Report</h1>
          <div class="meta">Generated on ${new Date().toLocaleString()}</div>
          ${projectsHTML}
          <script>window.print(); window.onafterprint = () => window.close();</script>
        </body>
      </html>
    `);
    printWindow.document.close();
    setShowExportMenu(false);
  };

  const exportToWord = () => {
    const projectsHTML = filteredProjects.map(project => `
      <div style="margin-bottom: 30px; border: 1px solid #ddd; padding: 20px;">
        <h3>${project.name}</h3>
        <p><strong>Description:</strong> ${project.description || '-'}</p>
        <p><strong>Status:</strong> ${project.status.toUpperCase()}</p>
        <p><strong>Start Date:</strong> ${new Date(project.startDate).toLocaleDateString()}</p>
        <p><strong>Deadline:</strong> ${new Date(project.deadline).toLocaleDateString()}</p>
        <p><strong>Progress:</strong> ${calculateProgress(project)}%</p>
        <p><strong>Assigned Employees:</strong> ${project.assignedEmployees.map(e => `${e.firstName} ${e.lastName} (${e.employeeId})`).join(', ')}</p>
        <p><strong>Documents:</strong> ${project.documents?.length || 0}</p>
      </div>
    `).join('');

    const htmlContent = `
      <!DOCTYPE html>
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word'>
        <head><meta charset='utf-8'><title>Projects Report</title></head>
        <body>
          <h1 style="text-align: center; color: #667eea;">📋 Projects Report</h1>
          <p style="text-align: center; color: #666;">Generated on ${new Date().toLocaleString()}</p>
          ${projectsHTML}
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'application/msword' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `projects_${new Date().toISOString().split('T')[0]}.doc`;
    link.click();
    setShowExportMenu(false);
  };

  // Filter projects based on search term, overdue status, month, and employee
  const filteredProjects = projects.filter(
    (project) => {
      // Search term filter
      const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (project.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      // Overdue filter
      let matchesOverdue = true;
      if (filterOverdue === 'overdue') {
        matchesOverdue = isProjectOverdue(project);
      } else if (filterOverdue === 'not-overdue') {
        matchesOverdue = !isProjectOverdue(project);
      }
      
      // Month filter
      let matchesMonth = true;
      if (filterMonth !== 'all') {
        const projectDate = new Date(project.startDate);
        const projectMonth = `${projectDate.getFullYear()}-${String(projectDate.getMonth() + 1).padStart(2, '0')}`;
        matchesMonth = projectMonth === filterMonth;
      }
      
      // Employee filter
      let matchesEmployee = true;
      if (filterEmployee !== 'all') {
        matchesEmployee = project.assignedEmployees.some(emp => emp._id === filterEmployee);
      }
      
      return matchesSearch && matchesOverdue && matchesMonth && matchesEmployee;
    }
  );

  // Filter employees based on employee search term
  const filteredEmployees = employees.filter(
    (emp) =>
      (emp.firstName + " " + emp.lastName)
        .toLowerCase()
        .includes(employeeSearchTerm.toLowerCase()) ||
      emp.employeeId.toLowerCase().includes(employeeSearchTerm.toLowerCase())
  );

  // Filter Sales employees
  const salesEmployees = employees.filter(
    (emp) =>
      emp.role?.name?.toLowerCase().includes("sales") &&
      ((emp.firstName + " " + emp.lastName)
        .toLowerCase()
        .includes(salesSearchTerm.toLowerCase()) ||
        emp.employeeId.toLowerCase().includes(salesSearchTerm.toLowerCase()))
  );

  // Filter Developer employees
  const developerEmployees = employees.filter(
    (emp) =>
      (emp.role?.name?.toLowerCase().includes("developer") ||
        emp.role?.name?.toLowerCase().includes("engineer") ||
        emp.role?.name?.toLowerCase().includes("programmer")) &&
      ((emp.firstName + " " + emp.lastName)
        .toLowerCase()
        .includes(developerSearchTerm.toLowerCase()) ||
        emp.employeeId
          .toLowerCase()
          .includes(developerSearchTerm.toLowerCase()))
  );

  return (
    <div className="container">
      {/* Header Row - Responsive */}
      <div style={{ 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "space-between",
        marginBottom: "20px",
        flexWrap: "wrap",
        gap: "12px"
      }}>
        <h1 style={{ margin: 0, fontSize: "clamp(20px, 5vw, 28px)", fontWeight: "700", whiteSpace: "nowrap" }}>Projects</h1>
        
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: "1 1 auto", justifyContent: "flex-end", flexWrap: "wrap", minWidth: "280px" }}>
          {/* Overdue Filter */}
          <select
            value={filterOverdue}
            onChange={(e) => setFilterOverdue(e.target.value)}
            style={{
              padding: "8px 12px",
              border: "1px solid #ddd",
              borderRadius: "8px",
              fontSize: "13px",
              minWidth: "130px",
              height: "38px",
              cursor: "pointer",
              background: "white",
            }}
          >
            <option value="all">All Projects</option>
            <option value="overdue">🔴 Overdue Only</option>
            <option value="not-overdue">✅ Not Overdue</option>
          </select>
          
          {/* Month Filter */}
          <select
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
            style={{
              padding: "8px 12px",
              border: "1px solid #ddd",
              borderRadius: "8px",
              fontSize: "13px",
              minWidth: "130px",
              height: "38px",
              cursor: "pointer",
              background: "white",
            }}
          >
            <option value="all">All Months</option>
            {/* Generate last 12 months */}
            {Array.from({ length: 12 }, (_, i) => {
              const date = new Date();
              date.setMonth(date.getMonth() - i);
              const monthValue = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
              const monthLabel = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
              return (
                <option key={monthValue} value={monthValue}>
                  {monthLabel}
                </option>
              );
            })}
          </select>
          
          {/* Employee Filter */}
          <select
            value={filterEmployee}
            onChange={(e) => setFilterEmployee(e.target.value)}
            style={{
              padding: "8px 12px",
              border: "1px solid #ddd",
              borderRadius: "8px",
              fontSize: "13px",
              minWidth: "150px",
              height: "38px",
              cursor: "pointer",
              background: "white",
            }}
          >
            <option value="all">All Employees</option>
            {employees
              .sort((a, b) => {
                const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
                const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
                return nameA.localeCompare(nameB);
              })
              .map((emp) => (
                <option key={emp._id} value={emp._id}>
                  {emp.firstName} {emp.lastName} ({emp.employeeId})
                </option>
              ))}
          </select>
          
          {/* View Toggle Buttons */}
          <button
            onClick={() => setViewMode('card')}
            style={{
              padding: "8px 14px",
              border: "1px solid #ddd",
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              background: viewMode === 'card' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
              color: viewMode === 'card' ? 'white' : '#64748b',
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              gap: "5px",
              height: "38px",
              whiteSpace: "nowrap"
            }}
          >
            🧩 Card View
          </button>
          <button
            onClick={() => setViewMode('table')}
            style={{
              padding: "8px 14px",
              border: "1px solid #ddd",
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              background: viewMode === 'table' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
              color: viewMode === 'table' ? 'white' : '#64748b',
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              gap: "5px",
              height: "38px",
              whiteSpace: "nowrap"
            }}
          >
            📋 Table View
          </button>
          
          {/* Export Button */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              style={{
                padding: "8px 14px",
                border: "none",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: "600",
                cursor: "pointer",
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: 'white',
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                gap: "5px",
                boxShadow: '0 2px 8px rgba(17, 153, 142, 0.3)',
                height: "38px",
                whiteSpace: "nowrap"
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              📥 Export {showExportMenu ? '▲' : '▼'}
            </button>
            
            {showExportMenu && (
              <div style={{
                position: 'absolute',
                top: '48px',
                right: '0',
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 1000,
                minWidth: '160px',
                overflow: 'hidden'
              }}>
                <button
                  onClick={exportToPDF}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: 'none',
                    background: 'white',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  📄 Export as PDF
                </button>
                <button
                  onClick={exportToExcel}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: 'none',
                    background: 'white',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  📊 Export as Excel
                </button>
                <button
                  onClick={exportToWord}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: 'none',
                    background: 'white',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  📝 Export as Word
                </button>
              </div>
            )}
          </div>
          
          {isAdmin && (
            <button
              className="btn btn-primary"
              onClick={() => {
                setEditingProject(null);
                setSelectedFiles([]);
                setFormData({
                  name: "",
                  description: "",
                  assignedEmployees: [],
                  startDate: "",
                  deadline: "",
                  status: "active",
                });
                setShowModal(true);
              }}
            >
              Create Project
            </button>
          )}
        </div>
      </div>

      {/* Card View */}
      {viewMode === 'card' && (
        <div>
        {filteredProjects.length === 0 ? (
          <div className="card">
            <p style={{ textAlign: "center", color: "#666" }}>
              {searchTerm
                ? `No projects found matching "${searchTerm}"`
                : "No projects found"}
            </p>
          </div>
        ) : (
          filteredProjects.map((project) => {
            const isOverdue = isProjectOverdue(project);
            
            return (
              <div
                key={project._id}
                className={`card ${isOverdue ? 'overdue-blink' : ''}`}
                style={{
                  marginBottom: "20px",
                  backgroundColor: isOverdue ? '#ffebee' : 'white',
                  border: isOverdue ? '2px solid #f44336' : '1px solid #ddd'
                }}
              >
              <div className="flex-between">
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "15px",
                      marginBottom: "10px",
                    }}
                  >
                    <h3 style={{ margin: 0 }}>{project.name}</h3>
                    <span
                      style={{
                        padding: "4px 12px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#fff",
                        backgroundColor: isOverdue ? "#f44336" : getStatusColor(project.status),
                      }}
                    >
                      {isOverdue ? "⚠️ OVERDUE" : project.status.toUpperCase()}
                    </span>
                  </div>

                  {project.description && (
                    <p
                      style={{
                        fontSize: "14px",
                        color: "#555",
                        marginTop: "10px",
                        lineHeight: "1.5",
                      }}
                    >
                      {project.description}
                    </p>
                  )}

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "10px",
                      marginTop: "15px",
                    }}
                  >
                    <div>
                      <p
                        style={{
                          fontSize: "12px",
                          color: "#999",
                          marginBottom: "5px",
                        }}
                      >
                        Start Date
                      </p>
                      <p style={{ fontSize: "14px", color: "#333" }}>
                        {new Date(project.startDate).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p
                        style={{
                          fontSize: "12px",
                          color: "#999",
                          marginBottom: "5px",
                        }}
                      >
                        Deadline
                      </p>
                      <p style={{ fontSize: "14px", color: "#333" }}>
                        {new Date(project.deadline).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <div style={{ marginTop: "15px" }}>
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#999",
                        marginBottom: "8px",
                      }}
                    >
                      Assigned Employees
                    </p>
                    <div
                      style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
                    >
                      {project.assignedEmployees.map((emp) => (
                        <span
                          key={emp._id}
                          style={{
                            padding: "4px 10px",
                            background: "#e9ecef",
                            borderRadius: "8px",
                            fontSize: "13px",
                            color: "#495057",
                          }}
                        >
                          {emp.firstName} {emp.lastName} ({emp.employeeId})
                        </span>
                      ))}
                    </div>
                  </div>

                  {project.documents && project.documents.length > 0 && (
                    <div style={{ marginTop: "15px" }}>
                      <p
                        style={{
                          fontSize: "12px",
                          color: "#999",
                          marginBottom: "8px",
                        }}
                      >
                        Documents
                      </p>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        {project.documents.map((doc) => (
                          <div
                            key={doc._id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "8px 12px",
                              background: "#f8f9fa",
                              borderRadius: "6px",
                              border: "1px solid #dee2e6",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <span style={{ fontSize: "20px" }}>
                                {doc.originalName.endsWith(".pdf")
                                  ? "📄"
                                  : "📝"}
                              </span>
                              <div>
                                <p
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: "500",
                                    margin: 0,
                                  }}
                                >
                                  {doc.originalName}
                                </p>
                                <p
                                  style={{
                                    fontSize: "11px",
                                    color: "#6c757d",
                                    margin: 0,
                                  }}
                                >
                                  {(doc.size / 1024).toFixed(2)} KB •{" "}
                                  {new Date(
                                    doc.uploadedAt
                                  ).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: "8px" }}>
                              <button
                                onClick={() =>
                                  downloadDocument(
                                    project._id,
                                    doc._id,
                                    doc.originalName
                                  )
                                }
                                style={{
                                  padding: "4px 12px",
                                  fontSize: "12px",
                                  background: "#007bff",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                }}
                              >
                                Download
                              </button>
                              {isAdmin && (
                                <button
                                  onClick={() =>
                                    deleteDocument(project._id, doc._id)
                                  }
                                  style={{
                                    padding: "4px 12px",
                                    fontSize: "12px",
                                    background: "#dc3545",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                  }}
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div
                  style={{ display: "flex", gap: "10px", marginLeft: "20px", alignItems: "center" }}
                >
                  <button
                    onClick={() => handleViewDetails(project)}
                    style={{
                      padding: "8px 16px",
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontWeight: "600",
                      fontSize: "14px"
                    }}
                  >
                    👁️ View
                  </button>
                  {isAdmin && (
                    <>
                      <button
                        onClick={() => handleEdit(project)}
                        className="btn btn-secondary"
                        style={{ padding: "8px 16px" }}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleDelete(project._id)}
                        className="btn"
                        style={{
                          padding: "8px 16px",
                          background: "#dc3545",
                          color: "white",
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })
        )}
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedProject && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div
            className="modal-content"
            style={{ maxWidth: "900px", maxHeight: "90vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: "24px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                borderRadius: "12px 12px 0 0",
                marginBottom: "24px"
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h2 style={{ margin: "0 0 8px 0", fontSize: "28px", fontWeight: "700" }}>
                    {selectedProject.name}
                  </h2>
                  <p style={{ margin: 0, fontSize: "14px", opacity: 0.9 }}>
                    Project Details & Progress Tracking
                  </p>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  style={{
                    background: "rgba(255,255,255,0.2)",
                    border: "none",
                    color: "white",
                    fontSize: "24px",
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    cursor: "pointer",
                    transition: "all 0.2s"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.3)"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.2)"}
                >
                  ×
                </button>
              </div>
            </div>

            <div style={{ padding: "0 24px 24px" }}>
              {/* Project Info Grid */}
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "20px",
                marginBottom: "24px"
              }}>
                <div style={{
                  background: "#f8f9fa",
                  padding: "16px",
                  borderRadius: "8px",
                  border: "1px solid #e9ecef"
                }}>
                  <p style={{ fontSize: "12px", color: "#6c757d", margin: "0 0 8px 0", fontWeight: "600" }}>STATUS</p>
                  <span style={{
                    padding: "6px 14px",
                    borderRadius: "20px",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#fff",
                    background: getStatusColor(selectedProject.status),
                    display: "inline-block"
                  }}>
                    {selectedProject.status === 'active' ? '🟢 Active' :
                     selectedProject.status === 'completed' ? '🟦 Completed' :
                     '🟡 On Hold'}
                  </span>
                </div>

                <div style={{
                  background: "#f8f9fa",
                  padding: "16px",
                  borderRadius: "8px",
                  border: "1px solid #e9ecef"
                }}>
                  <p style={{ fontSize: "12px", color: "#6c757d", margin: "0 0 8px 0", fontWeight: "600" }}>ASSIGNED TEAM</p>
                  <p style={{ fontSize: "20px", fontWeight: "700", color: "#667eea", margin: 0 }}>
                    👥 {selectedProject.assignedEmployees.length} {selectedProject.assignedEmployees.length === 1 ? 'Person' : 'People'}
                  </p>
                </div>

                <div style={{
                  background: "#f8f9fa",
                  padding: "16px",
                  borderRadius: "8px",
                  border: "1px solid #e9ecef"
                }}>
                  <p style={{ fontSize: "12px", color: "#6c757d", margin: "0 0 8px 0", fontWeight: "600" }}>START DATE</p>
                  <p style={{ fontSize: "16px", fontWeight: "600", color: "#333", margin: 0 }}>
                    📅 {new Date(selectedProject.startDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </p>
                </div>

                <div style={{
                  background: "#f8f9fa",
                  padding: "16px",
                  borderRadius: "8px",
                  border: "1px solid #e9ecef"
                }}>
                  <p style={{ fontSize: "12px", color: "#6c757d", margin: "0 0 8px 0", fontWeight: "600" }}>DEADLINE</p>
                  <p style={{ fontSize: "16px", fontWeight: "600", color: "#333", margin: 0 }}>
                    ⏰ {new Date(selectedProject.deadline).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </p>
                </div>
              </div>

              {/* Description */}
              {selectedProject.description && (
                <div style={{
                  background: "#fff3cd",
                  padding: "16px",
                  borderRadius: "8px",
                  border: "1px solid #ffecb5",
                  marginBottom: "24px"
                }}>
                  <p style={{ fontSize: "12px", color: "#856404", margin: "0 0 8px 0", fontWeight: "600" }}>📝 DESCRIPTION</p>
                  <p style={{ fontSize: "14px", color: "#856404", margin: 0, lineHeight: "1.6" }}>
                    {selectedProject.description}
                  </p>
                </div>
              )}

              {/* Team Members */}
              <div style={{ marginBottom: "24px" }}>
                <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#333", marginBottom: "16px" }}>
                  👥 Team Members ({selectedProject.assignedEmployees.length})
                </h3>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                  gap: "12px"
                }}>
                  {selectedProject.assignedEmployees.map((emp) => (
                    <div
                      key={emp._id}
                      style={{
                        background: "linear-gradient(135deg, #667eea15 0%, #764ba215 100%)",
                        padding: "14px",
                        borderRadius: "8px",
                        border: "1px solid #667eea30",
                        display: "flex",
                        alignItems: "center",
                        gap: "12px"
                      }}
                    >
                      <div style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontWeight: "700",
                        fontSize: "16px"
                      }}>
                        {emp.firstName.charAt(0)}{emp.lastName.charAt(0)}
                      </div>
                      <div>
                        <p style={{ margin: 0, fontWeight: "600", fontSize: "14px", color: "#333" }}>
                          {emp.firstName} {emp.lastName}
                        </p>
                        <p style={{ margin: 0, fontSize: "12px", color: "#6c757d" }}>
                          ID: {emp.employeeId}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Documents */}
              {selectedProject.documents && selectedProject.documents.length > 0 && (
                <div>
                  <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#333", marginBottom: "16px" }}>
                    📎 Project Documents ({selectedProject.documents.length})
                  </h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    {selectedProject.documents.map((doc) => (
                      <div
                        key={doc._id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "14px",
                          background: "#f8f9fa",
                          borderRadius: "8px",
                          border: "1px solid #dee2e6"
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          <span style={{ fontSize: "28px" }}>
                            {doc.originalName.endsWith(".pdf") ? "📄" : "📝"}
                          </span>
                          <div>
                            <p style={{ margin: 0, fontWeight: "600", fontSize: "14px", color: "#333" }}>
                              {doc.originalName}
                            </p>
                            <p style={{ margin: 0, fontSize: "12px", color: "#6c757d" }}>
                              {(doc.size / 1024).toFixed(2)} KB • {new Date(doc.uploadedAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => downloadDocument(selectedProject._id, doc._id, doc.originalName)}
                          style={{
                            padding: "8px 16px",
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "600",
                            cursor: "pointer"
                          }}
                        >
                          📥 Download
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {filteredProjects.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <p style={{ color: "#666" }}>
                {searchTerm
                  ? `No projects found matching "${searchTerm}"`
                  : "No projects found"}
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white'
                  }}>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', fontSize: '14px', whiteSpace: 'nowrap' }}>Project Name</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', fontSize: '14px', whiteSpace: 'nowrap' }}>Description</th>
                    <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', fontSize: '14px', whiteSpace: 'nowrap' }}>Status</th>
                    <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', fontSize: '14px', whiteSpace: 'nowrap' }}>Start Date</th>
                    <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', fontSize: '14px', whiteSpace: 'nowrap' }}>Deadline</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', fontSize: '14px', whiteSpace: 'nowrap' }}>Assigned Employees</th>
                    <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', fontSize: '14px', whiteSpace: 'nowrap' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.map((project) => {
                    const isOverdue = isProjectOverdue(project);
                    
                    return (
                      <tr 
                        key={project._id}
                        style={{
                          borderBottom: '1px solid #e2e8f0',
                          background: isOverdue ? '#fff5f5' : 'white',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = isOverdue ? '#fee2e2' : '#f8fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.background = isOverdue ? '#fff5f5' : 'white'}
                      >
                        <td style={{ padding: '16px', fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>
                          {project.name}
                          {isOverdue && (
                            <span style={{
                              marginLeft: '8px',
                              padding: '2px 8px',
                              background: '#fee2e2',
                              color: '#991b1b',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '700'
                            }}>
                              ⚠️ OVERDUE
                            </span>
                          )}
                        </td>
                        <td style={{ 
                          padding: '16px', 
                          color: '#64748b', 
                          fontSize: '13px',
                          maxWidth: '300px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {project.description || '-'}
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{
                            padding: '6px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#fff',
                            background: getStatusColor(project.status),
                            display: 'inline-block',
                            whiteSpace: 'nowrap'
                          }}>
                            {project.status === 'active' ? '🟢 Active' : 
                             project.status === 'completed' ? '🟦 Completed' : 
                             '🟡 On Hold'}
                          </span>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center', fontSize: '13px', color: '#475569', whiteSpace: 'nowrap' }}>
                          {new Date(project.startDate).toLocaleDateString('en-GB')}
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center', fontSize: '13px', color: '#475569', whiteSpace: 'nowrap' }}>
                          {new Date(project.deadline).toLocaleDateString('en-GB')}
                        </td>
                        <td style={{ padding: '16px' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '250px' }}>
                            {project.assignedEmployees.slice(0, 3).map((emp) => (
                              <span
                                key={emp._id}
                                style={{
                                  padding: '4px 8px',
                                  background: '#e9ecef',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  color: '#495057',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {emp.firstName} {emp.lastName}
                              </span>
                            ))}
                            {project.assignedEmployees.length > 3 && (
                              <span style={{
                                padding: '4px 8px',
                                background: '#667eea',
                                color: 'white',
                                borderRadius: '6px',
                                fontSize: '11px',
                                fontWeight: '600'
                              }}>
                                +{project.assignedEmployees.length - 3}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => handleViewDetails(project)}
                              style={{
                                padding: '6px 12px',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                            >
                              👁️ View
                            </button>
                            {isAdmin && (
                              <>
                                <button
                                  onClick={() => handleEdit(project)}
                                  style={{
                                    padding: '6px 12px',
                                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    whiteSpace: 'nowrap'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                >
                                  ✏️ Edit
                                </button>
                                <button
                                  onClick={() => handleDelete(project._id)}
                                  style={{
                                    padding: '6px 12px',
                                    background: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    whiteSpace: 'nowrap'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                >
                                  🗑️ Delete
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div
            className="modal-content"
            style={{ maxHeight: "90vh", overflowY: "auto", maxWidth: "700px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: "20px",
                borderBottom: "1px solid #eee",
                marginBottom: "20px",
              }}
            >
              <h2 style={{ margin: 0, fontSize: "24px", fontWeight: "600" }}>
                {editingProject ? "✏️ Edit Project" : "➕ Create New Project"}
              </h2>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: "0 20px" }}>
              {/* Basic Info Section */}
              <div style={{ marginBottom: "25px" }}>
                <h4
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#667eea",
                    marginBottom: "15px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  📋 Basic Information
                </h4>

                <div className="form-group">
                  <label
                    style={{
                      fontWeight: "600",
                      marginBottom: "8px",
                      display: "block",
                    }}
                  >
                    Project Name <span style={{ color: "#dc3545" }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="Enter project name"
                    required
                    style={{
                      padding: "10px 12px",
                      borderRadius: "6px",
                      border: "1px solid #ddd",
                    }}
                  />
                </div>

                <div className="form-group">
                  <label
                    style={{
                      fontWeight: "600",
                      marginBottom: "8px",
                      display: "block",
                    }}
                  >
                    Description
                  </label>
                  <textarea
                    className="form-control"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Enter project description"
                    rows="4"
                    style={{
                      padding: "10px 12px",
                      borderRadius: "6px",
                      border: "1px solid #ddd",
                      fontFamily: "inherit",
                      resize: "vertical",
                    }}
                  />
                </div>
              </div>

              {/* Timeline Section */}
              <div style={{ marginBottom: "25px" }}>
                <h4
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#667eea",
                    marginBottom: "15px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  📅 Timeline
                </h4>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "15px",
                  }}
                >
                  <div className="form-group">
                    <label
                      style={{
                        fontWeight: "600",
                        marginBottom: "8px",
                        display: "block",
                      }}
                    >
                      Start Date <span style={{ color: "#dc3545" }}>*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.startDate}
                      onChange={(e) =>
                        setFormData({ ...formData, startDate: e.target.value })
                      }
                      required
                      style={{
                        padding: "10px 12px",
                        borderRadius: "6px",
                        border: "1px solid #ddd",
                        width: "100%",
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label
                      style={{
                        fontWeight: "600",
                        marginBottom: "8px",
                        display: "block",
                      }}
                    >
                      Deadline <span style={{ color: "#dc3545" }}>*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.deadline}
                      onChange={(e) =>
                        setFormData({ ...formData, deadline: e.target.value })
                      }
                      required
                      style={{
                        padding: "10px 12px",
                        borderRadius: "6px",
                        border: "1px solid #ddd",
                        width: "100%",
                      }}
                    />
                  </div>
                </div>

                <div className="form-group" style={{ marginTop: "15px" }}>
                  <label
                    style={{
                      fontWeight: "600",
                      marginBottom: "8px",
                      display: "block",
                    }}
                  >
                    Status
                  </label>
                  <select
                    className="form-control"
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                    style={{
                      padding: "10px 12px",
                      borderRadius: "6px",
                      border: "1px solid #ddd",
                      width: "100%",
                    }}
                  >
                    <option value="active">🟢 Active</option>
                    <option value="on-hold">🟡 On Hold</option>
                    <option value="completed">🟦 Completed</option>
                  </select>
                </div>
              </div>

              {/* Employees Section */}
              <div style={{ marginBottom: "25px" }}>
                <h4
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#667eea",
                    marginBottom: "15px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  👥 Assign Employees ({formData.assignedEmployees.length})
                </h4>

                {/* Employee Search Bars */}
                <div
                  style={{
                    marginBottom: "15px",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "10px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12px",
                        fontWeight: "600",
                        marginBottom: "5px",
                        color: "#667eea",
                      }}
                    >
                      🔍 All Employees
                    </label>
                    <input
                      type="text"
                      placeholder="Search all employees..."
                      value={employeeSearchTerm}
                      onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #ddd",
                        borderRadius: "5px",
                        fontSize: "14px",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12px",
                        fontWeight: "600",
                        marginBottom: "5px",
                        color: "#28a745",
                      }}
                    >
                      💼 Sales Team
                    </label>
                    <input
                      type="text"
                      placeholder="Search sales employees..."
                      value={salesSearchTerm}
                      onChange={(e) => setSalesSearchTerm(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #ddd",
                        borderRadius: "5px",
                        fontSize: "14px",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12px",
                        fontWeight: "600",
                        marginBottom: "5px",
                        color: "#dc3545",
                      }}
                    >
                      💻 Developers
                    </label>
                    <input
                      type="text"
                      placeholder="Search developers..."
                      value={developerSearchTerm}
                      onChange={(e) => setDeveloperSearchTerm(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #ddd",
                        borderRadius: "5px",
                        fontSize: "14px",
                      }}
                    />
                  </div>
                </div>

                <div
                  style={{
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    maxHeight: "300px",
                    overflowY: "auto",
                    padding: "12px",
                    background: "#fafafa",
                  }}
                >
                  {/* All Employees Section */}
                  {employeeSearchTerm && (
                    <div>
                      <h5
                        style={{
                          margin: "0 0 10px 0",
                          color: "#667eea",
                          fontSize: "14px",
                        }}
                      >
                        🔍 All Employees ({filteredEmployees.length})
                      </h5>
                      {filteredEmployees.length === 0 ? (
                        <p
                          style={{
                            color: "#999",
                            fontSize: "13px",
                            margin: "0 0 15px 0",
                            fontStyle: "italic",
                          }}
                        >
                          No employees found matching "{employeeSearchTerm}"
                        </p>
                      ) : (
                        filteredEmployees.map((emp) => (
                          <div
                            key={`all-${emp._id}`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px",
                              marginBottom: "6px",
                              background: "white",
                              borderRadius: "4px",
                              cursor: "pointer",
                              transition: "all 0.2s",
                              border: "1px solid #e3f2fd",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={formData.assignedEmployees.includes(
                                emp._id
                              )}
                              onChange={() => handleEmployeeToggle(emp._id)}
                              style={{
                                marginRight: "10px",
                                width: "16px",
                                height: "16px",
                                cursor: "pointer",
                              }}
                            />
                            <label
                              style={{
                                cursor: "pointer",
                                flex: 1,
                                margin: 0,
                                fontSize: "13px",
                              }}
                            >
                              <span
                                style={{ fontWeight: "500", color: "#333" }}
                              >
                                {emp.firstName} {emp.lastName}
                              </span>
                              <span
                                style={{
                                  fontSize: "11px",
                                  color: "#999",
                                  marginLeft: "8px",
                                }}
                              >
                                ({emp.employeeId}) -{" "}
                                {emp.role?.name || "No Role"}
                              </span>
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* Sales Team Section */}
                  {salesSearchTerm && (
                    <div>
                      <h5
                        style={{
                          margin: "0 0 10px 0",
                          color: "#28a745",
                          fontSize: "14px",
                        }}
                      >
                        💼 Sales Team ({salesEmployees.length})
                      </h5>
                      {salesEmployees.length === 0 ? (
                        <p
                          style={{
                            color: "#999",
                            fontSize: "13px",
                            margin: "0 0 15px 0",
                            fontStyle: "italic",
                          }}
                        >
                          No sales employees found matching "{salesSearchTerm}"
                        </p>
                      ) : (
                        salesEmployees.map((emp) => (
                          <div
                            key={`sales-${emp._id}`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px",
                              marginBottom: "6px",
                              background: "#f8fff9",
                              borderRadius: "4px",
                              cursor: "pointer",
                              transition: "all 0.2s",
                              border: "1px solid #d4edda",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={formData.assignedEmployees.includes(
                                emp._id
                              )}
                              onChange={() => handleEmployeeToggle(emp._id)}
                              style={{
                                marginRight: "10px",
                                width: "16px",
                                height: "16px",
                                cursor: "pointer",
                              }}
                            />
                            <label
                              style={{
                                cursor: "pointer",
                                flex: 1,
                                margin: 0,
                                fontSize: "13px",
                              }}
                            >
                              <span
                                style={{ fontWeight: "500", color: "#333" }}
                              >
                                {emp.firstName} {emp.lastName}
                              </span>
                              <span
                                style={{
                                  fontSize: "11px",
                                  color: "#28a745",
                                  marginLeft: "8px",
                                }}
                              >
                                ({emp.employeeId}) - {emp.role?.name}
                              </span>
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* Developers Section */}
                  {developerSearchTerm && (
                    <div>
                      <h5
                        style={{
                          margin: "0 0 10px 0",
                          color: "#dc3545",
                          fontSize: "14px",
                        }}
                      >
                        💻 Developers ({developerEmployees.length})
                      </h5>
                      {developerEmployees.length === 0 ? (
                        <p
                          style={{
                            color: "#999",
                            fontSize: "13px",
                            margin: "0 0 15px 0",
                            fontStyle: "italic",
                          }}
                        >
                          No developers found matching "{developerSearchTerm}"
                        </p>
                      ) : (
                        developerEmployees.map((emp) => (
                          <div
                            key={`dev-${emp._id}`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px",
                              marginBottom: "6px",
                              background: "#fff5f5",
                              borderRadius: "4px",
                              cursor: "pointer",
                              transition: "all 0.2s",
                              border: "1px solid #f8d7da",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={formData.assignedEmployees.includes(
                                emp._id
                              )}
                              onChange={() => handleEmployeeToggle(emp._id)}
                              style={{
                                marginRight: "10px",
                                width: "16px",
                                height: "16px",
                                cursor: "pointer",
                              }}
                            />
                            <label
                              style={{
                                cursor: "pointer",
                                flex: 1,
                                margin: 0,
                                fontSize: "13px",
                              }}
                            >
                              <span
                                style={{ fontWeight: "500", color: "#333" }}
                              >
                                {emp.firstName} {emp.lastName}
                              </span>
                              <span
                                style={{
                                  fontSize: "11px",
                                  color: "#dc3545",
                                  marginLeft: "8px",
                                }}
                              >
                                ({emp.employeeId}) - {emp.role?.name}
                              </span>
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* Default view when no search terms */}
                  {!employeeSearchTerm &&
                    !salesSearchTerm &&
                    !developerSearchTerm && (
                      <div>
                        {employees.length === 0 ? (
                          <p
                            style={{
                              color: "#999",
                              fontSize: "14px",
                              margin: 0,
                              textAlign: "center",
                              padding: "20px",
                            }}
                          >
                            No employees available
                          </p>
                        ) : (
                          <div>
                            <p
                              style={{
                                color: "#666",
                                fontSize: "13px",
                                margin: "0 0 15px 0",
                                textAlign: "center",
                                fontStyle: "italic",
                              }}
                            >
                              
                            </p>
                            {employees.map((emp) => (
                              <div
                                key={emp._id}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  padding: "10px",
                                  marginBottom: "8px",
                                  background: "white",
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                  border: "1px solid transparent",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.border =
                                    "1px solid #667eea";
                                  e.currentTarget.style.background = "#f8f9ff";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.border =
                                    "1px solid transparent";
                                  e.currentTarget.style.background = "white";
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={formData.assignedEmployees.includes(
                                    emp._id
                                  )}
                                  onChange={() => handleEmployeeToggle(emp._id)}
                                  style={{
                                    marginRight: "12px",
                                    width: "18px",
                                    height: "18px",
                                    cursor: "pointer",
                                  }}
                                />
                                <label
                                  style={{
                                    cursor: "pointer",
                                    flex: 1,
                                    margin: 0,
                                  }}
                                >
                                  <span
                                    style={{ fontWeight: "500", color: "#333" }}
                                  >
                                    {emp.firstName} {emp.lastName}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "12px",
                                      color: "#999",
                                      marginLeft: "8px",
                                    }}
                                  >
                                    ({emp.employeeId}) -{" "}
                                    {emp.role?.name || "No Role"}
                                  </span>
                                </label>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                </div>
              </div>

              {/* Documents Section */}
              <div style={{ marginBottom: "25px" }}>
                <h4
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#667eea",
                    marginBottom: "15px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  📎 Upload Documents
                </h4>

                <div
                  style={{
                    border: "2px dashed #667eea",
                    borderRadius: "6px",
                    padding: "20px",
                    textAlign: "center",
                    background: "#f8f9ff",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#e9ecef";
                    e.currentTarget.style.borderColor = "#764ba2";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#f8f9ff";
                    e.currentTarget.style.borderColor = "#667eea";
                  }}
                >
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                    id="file-input"
                  />
                  <label
                    htmlFor="file-input"
                    style={{ cursor: "pointer", display: "block" }}
                  >
                    <p
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#667eea",
                        margin: "0 0 5px 0",
                      }}
                    >
                      📁 Click to upload or drag and drop
                    </p>
                    <p style={{ fontSize: "13px", color: "#999", margin: 0 }}>
                      PDF, DOCX up to 10MB each • Maximum 5 files
                    </p>
                  </label>
                </div>

                {selectedFiles.length > 0 && (
                  <div style={{ marginTop: "15px" }}>
                    <p
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        marginBottom: "10px",
                        color: "#333",
                      }}
                    >
                      ✅ Selected Files ({selectedFiles.length}):
                    </p>
                    {selectedFiles.map((file, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "10px 12px",
                          background: "#e8f5e9",
                          borderRadius: "4px",
                          marginBottom: "8px",
                          border: "1px solid #81c784",
                        }}
                      >
                        <span style={{ fontSize: "13px", color: "#333" }}>
                          📄 {file.name} ({(file.size / 1024).toFixed(2)} KB)
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          style={{
                            background: "transparent",
                            border: "none",
                            color: "#dc3545",
                            cursor: "pointer",
                            fontSize: "18px",
                            padding: "0 5px",
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {editingProject &&
                  editingProject.documents &&
                  editingProject.documents.length > 0 && (
                    <div style={{ marginTop: "15px" }}>
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          marginBottom: "10px",
                          color: "#333",
                        }}
                      >
                        📦 Existing Documents:
                      </p>
                      {editingProject.documents.map((doc) => (
                        <div
                          key={doc._id}
                          style={{
                            padding: "10px 12px",
                            background: "#f0f4ff",
                            borderRadius: "4px",
                            marginBottom: "8px",
                            fontSize: "13px",
                            border: "1px solid #667eea",
                          }}
                        >
                          📄 {doc.originalName}
                        </div>
                      ))}
                    </div>
                  )}
              </div>

              {/* Buttons */}
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "flex-end",
                  marginTop: "25px",
                  paddingTop: "20px",
                  borderTop: "1px solid #eee",
                  paddingBottom: "20px",
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingProject(null);
                  }}
                  className="btn btn-secondary"
                  style={{
                    padding: "10px 24px",
                    fontWeight: "600",
                    borderRadius: "6px",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{
                    padding: "10px 24px",
                    fontWeight: "600",
                    borderRadius: "6px",
                  }}
                >
                  {editingProject ? "💾 Update Project" : "➕ Create Project"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Projects;
